/**
 * Fly-in Config Components - Fixed Version
 * Addresses empty component issues and improves error handling
 */
import { Component, registerComponent } from 'js/component.js';

const componentName = 'fly-in-config';

class FlyIn extends Component {

    /**
     * Component Setup
     */
    elements = {
        body: document.body
    };

    /**
     * Initialize Component
     */
    init() {
        let configPagePath = this.element.getAttribute('data-path');
        const isAuthorRunMode = this.element.getAttribute('data-author-run-mode');

        // Validate required config path
        if (!configPagePath) {
            console.warn('Fly-in config: Missing data-path attribute');
            return;
        }

        if (isAuthorRunMode && isAuthorRunMode === 'true') { // Author Environment
            configPagePath += '?wcmmode=disabled';
        }

        this.getFlyInConfigPage(configPagePath);
    }

    getFlyInConfigPage = (url) => {
        fetch(url).then((response) => {
            if (!response.ok) {
                throw new Error(`Response failed with status: ${response.status}`);
            }
            return response.text();
        }).then((response) => {
            // Parse HTML for fly-in components
            const flyInConfigPage = response;

            // Create a temporary container to parse HTML safely
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = flyInConfigPage;

            // Find fly-in components in the parsed HTML
            const flyInComponents = [...tempContainer.querySelectorAll('.fly-in')];

            if (flyInComponents.length === 0) {
                console.info('No fly-in components found in config page');
                return;
            }

            const flyInToDisplay = this.findFlyInToDisplay(flyInComponents);

            if (flyInToDisplay !== -1 && this.elements.body) {
                const selectedFlyIn = flyInComponents[flyInToDisplay];

                // Validate the selected fly-in before inserting
                if (this.validateFlyInComponent(selectedFlyIn)) {
                    // Fix: Use appendChild instead of insertAdjacentElement with
                    // beforeBegin on body can cause issues in some browsers
                    this.elements.body.appendChild(selectedFlyIn);

                    // Dispatch event to initialize the fly-in
                    window.dispatchEvent(new Event('fly-in-init'));
                } else {
                    console.warn('Selected fly-in component failed validation - not displaying');
                }
            } else if (flyInToDisplay === -1) {
                console.info('No matching fly-in found for current page and time');
            }
        }).catch((error) => {
            console.warn('Error fetching fly-in config page:', error);
        });
    };

    /**
     * Validates that a fly-in component has the required structure and content
     * @param {HTMLElement} flyInComponent - The fly-in component to validate
     * @returns {boolean} - True if valid, false otherwise
     */
    validateFlyInComponent = (flyInComponent) => {
        if (!flyInComponent) {
            console.warn('Fly-in validation: Component is null or undefined');
            return false;
        }

        // Check for required container
        const container = flyInComponent.querySelector('.o-flyIn, #af-flyin');
        if (!container) {
            console.warn('Fly-in validation: Missing required container (.o-flyIn or #af-flyin)');
            return false;
        }

        // Check for required data attributes
        const flyInId = container.getAttribute('data-flyin-id');
        if (!flyInId) {
            console.warn('Fly-in validation: Missing required data-flyin-id attribute');
            return false;
        }

        // Check if component has meaningful content
        const hasTextContent = container.textContent?.trim().length > 0;
        const hasImages = container.querySelectorAll('img').length > 0;
        const hasButtons = container.querySelectorAll('button, .button, a[href], .fly-in__cta').length > 0;
        const hasInteractiveElements = container.querySelectorAll('input, select, textarea').length > 0;

        if (!hasTextContent && !hasImages && !hasButtons && !hasInteractiveElements) {
            console.warn('Fly-in validation: Component appears to be empty (no content, images, buttons, or interactive elements)');
            return false;
        }

        return true;
    };

    findFlyInToDisplay = (flyInComponents) => {
        let pagePath = window.location.pathname.split('.')[0];
        if (pagePath.includes('/content/preview')) {
            pagePath = pagePath.replace('/content/preview', '/content');
        }

        let flyInToDisplay = -1;
        let highestPriority = -1; // Track priority for multiple matches

        flyInComponents.forEach((flyInComponent, index) => {
            try {
                let excludePathMatch = false;
                let displayPathMatch = false;
                let validTime = false;

                // Look for container with multiple possible selectors
                const container = flyInComponent.querySelector('.o-flyIn')
                    || flyInComponent.querySelector('#af-flyin')
                    || flyInComponent.querySelector('[data-flyin-id]');

                if (!container) {
                    console.warn(`Fly-in ${index}: No valid container found`);
                    return;
                }

                const onTime = container.getAttribute('data-on-time');
                const offTime = container.getAttribute('data-off-time');
                const excludePaths = container.getAttribute('data-exclude-paths')
                    ? container.getAttribute('data-exclude-paths').split(',') : [];
                const displayPaths = container.getAttribute('data-display-paths')
                    ? container.getAttribute('data-display-paths').split(',') : [];
                const priority = parseInt(container.getAttribute('data-priority'), 10) || 0;

                // Validate time window
                validTime = this.isValidTimeWindow(onTime, offTime);
                if (!validTime) {
                    return;
                }

                // Check path matching
                excludePathMatch = this.isPathMatch(excludePaths, pagePath, true);
                displayPathMatch = this.isPathMatch(displayPaths, pagePath, false);

                // If this fly-in matches and has higher priority, select it
                if (
                    !excludePathMatch
                    && displayPathMatch
                    && validTime
                    && priority >= highestPriority
                ) {
                    flyInToDisplay = index;
                    highestPriority = priority;
                }

            } catch (error) {
                console.warn(`Error processing fly-in component ${index}:`, error);
            }
        });

        return flyInToDisplay;
    };

    /**
     * Check if current time is within the valid time window
     * @param {string} onTime - Start time string
     * @param {string} offTime - End time string
     * @returns {boolean} - True if time is valid
     */
    isValidTimeWindow = (onTime, offTime) => {
        // If no time restrictions, always valid
        if (!onTime && !offTime) {
            return true;
        }

        // If only one time is set, treat as invalid for safety
        if (!onTime || !offTime) {
            console.warn('Fly-in: Both on-time and off-time must be set if using time restrictions');
            return false;
        }

        try {
            const currentTime = new Date();
            const onTimeDate = new Date(onTime);
            const offTimeDate = new Date(offTime);

            // Validate dates are valid
            if (Number.isNaN(onTimeDate.getTime()) || Number.isNaN(offTimeDate.getTime())) {
                console.warn('Fly-in: Invalid date format in time restrictions');
                return false;
            }

            return currentTime >= onTimeDate && currentTime <= offTimeDate;
        } catch (error) {
            console.warn('Fly-in: Error parsing time restrictions:', error);
            return false;
        }
    };

    /**
     * Check if current page path matches any of the display paths
     * @param {Array} paths - Array of path patterns
     * @param {string} currentPath - Current page path
     * @param {boolean} isCheckingExcludePath - Is this checking for an exclusion path?
     * @returns {boolean} - True if path matches
     */
    isPathMatch = (paths, currentPath, isCheckingExcludePath = false) => {
        if (!paths || paths.length === 0) {
            // No path restrictions means show on all pages, for displayPaths.
            // Otherwise, false.
            return !isCheckingExcludePath;
        }

        return paths.some((displayPath) => {
            if (!displayPath || displayPath.trim() === '') {
                return false;
            }

            try {
                const path = displayPath.split('?includeChildren=')[0];
                const includeChildren = displayPath.split('?includeChildren=')[1] === 'true';

                if (includeChildren) {
                    return currentPath.startsWith(path);
                }
                return currentPath === path;
            } catch (error) {
                console.warn('Error processing display path:', displayPath, error);
                return false;
            }
        });
    };
}

registerComponent(`.${componentName}`, FlyIn);
